import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Star,
  Users,
  Zap,
  BarChart,
  Shield,
  Sparkles,
} from "lucide-react";
import Wrapper from "@/components/global/wrapper";
import Container from "@/components/global/container";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { FEATURES } from "@/constants/features";
// Import our new reusable components
import Section from "@/components/common/Section";
import StatsGrid from "@/components/common/StatsGrid";
import CTASection from "@/components/common/CTASection";
import FeatureCard from "@/components/common/FeatureCard";
import TestimonialCard from "@/components/common/TestimonialCard";
import { getFeatureColors } from "@/lib/design-tokens";

const FeatureDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();

  // Find the feature by slug
  const feature = FEATURES.find((f) => f.slug === slug);

  // If feature not found, redirect to features page
  if (!feature) {
    return <Navigate to="/features" replace data-oid="7t0sap1" />;
  }

  const Icon = feature.icon;
  const featureColors = getFeatureColors(feature.color);

  // Transform metrics for StatsGrid component
  const stats =
    feature.details?.metrics?.map((metric) => ({
      value: metric.value,
      label: metric.label,
      color: "text-blue-600",
    })) || [];

  // Mock testimonials for each feature
  const testimonials = [
    {
      quote: `${feature.title} has been a game-changer for our team. The results speak for themselves.`,
      author: "Sarah Martinez",
      role: "Marketing Director",
      company: "TechFlow Inc.",
      rating: 5,
      image: "/images/testimonials/sarah-martinez.jpg",
    },
    {
      quote: `The ${feature.title.toLowerCase()} capabilities have exceeded our expectations in every way.`,
      author: "David Chen",
      role: "CTO",
      company: "Innovation Labs",
      rating: 5,
      image: "/images/testimonials/david-chen.jpg",
    },
  ];

  // Mock related features
  const relatedFeatures = FEATURES.filter((f) => f.slug !== slug).slice(0, 3);

  return (
    <Wrapper className="py-20 relative" data-oid="fbolr8k">
      {/* Header */}
      <Container data-oid="80_4-k.">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          data-oid="xwix092"
        >
          <Link
            to="/features"
            className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors mb-6"
            data-oid=":u31r-p"
          >
            <ArrowLeft className="w-4 h-4 mr-2" data-oid="byjciaf" />
            Back to Features
          </Link>
        </motion.div>
      </Container>

      {/* Hero Section */}
      <Container data-oid="bdfs:2y">
        <div
          className="grid lg:grid-cols-2 gap-12 items-center mb-20"
          data-oid="nvcud6k"
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            data-oid="07-2v18"
          >
            <div
              className={`w-16 h-16 rounded-2xl ${featureColors.bg} p-3 mb-6`}
              data-oid="kqp8vr_"
            >
              <Icon
                className={`w-full h-full ${featureColors.text}`}
                data-oid="t3b9f:h"
              />
            </div>
            <h1
              className="text-4xl md:text-5xl font-bold mb-6"
              data-oid="ty3jyu_"
            >
              {feature.details?.heading || feature.title}
            </h1>
            <p
              className="text-xl text-muted-foreground mb-8"
              data-oid="1qkoa75"
            >
              {feature.details?.subheading || feature.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-oid="fp7egv9">
              <Link to="/demo" data-oid="pdqpd61">
                <Button size="lg" className="px-8">
                  Start Free Trial
                  <ArrowRight className="w-4 h-4 ml-2" data-oid="3.59yyf" />
                </Button>
              </Link>
              <Link to="/contact" data-oid="yfxcmah">
                <Button
                  size="lg"
                  variant="outline"
                  className="px-8"
                  data-oid="r0_zgws"
                >
                  Schedule Demo
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-oid=".l83lr3"
          >
            <img
              src={feature.image}
              alt={feature.title}
              className="rounded-2xl shadow-2xl w-full h-auto"
              onError={(e) => {
                e.currentTarget.style.background =
                  "linear-gradient(135deg, #3b82f6, #8b5cf6)";
                e.currentTarget.style.height = "400px";
                e.currentTarget.style.display = "block";
              }}
              data-oid="dwltjm1"
            />

            <div
              className="absolute inset-0 bg-gradient-to-t from-blue-600/10 to-transparent rounded-2xl"
              data-oid="bj-yd4h"
            ></div>
          </motion.div>
        </div>
      </Container>

      {/* Metrics Section - Using StatsGrid */}
      {stats.length > 0 && (
        <StatsGrid
          stats={stats}
          variant="gradient"
          columns={3}
          className="mb-20"
          data-oid="mihnmkj"
        />
      )}

      {/* Benefits Section */}
      {feature.details?.benefits && (
        <Section
          title="Key Benefits"
          subtitle={`Discover how ${feature.title} can transform your workflow and drive better results.`}
          spacing="large"
          data-oid="hg9oin1"
        >
          <div className="grid md:grid-cols-2 gap-12" data-oid="_.zr291">
            <div className="space-y-4" data-oid="9dmaeup">
              {feature.details.benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  data-oid="7v8nyzl"
                >
                  <Check
                    className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0"
                    data-oid="-7s8f79"
                  />
                  <span data-oid="srf17p.">{benefit}</span>
                </motion.div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4" data-oid="9047co2">
              {[Users, Zap, BarChart, Shield].map((IconComp, index) => (
                <motion.div
                  key={index}
                  className="p-6 rounded-xl border bg-card text-center hover:shadow-lg transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  data-oid="biy8s26"
                >
                  <IconComp
                    className="w-8 h-8 text-blue-600 mx-auto mb-3"
                    data-oid="6.cyzc5"
                  />
                  <div className="text-sm font-medium" data-oid="z9k0ubd">
                    Enhanced Productivity
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* Features List */}
      {feature.details?.features && (
        <Section
          title="Comprehensive Features"
          spacing="large"
          data-oid="vs8ud8c"
        >
          <div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-oid="kto10:n"
          >
            {feature.details.features.map((featureItem, index) => (
              <FeatureCard
                key={index}
                icon={Check}
                title={featureItem}
                description=""
                variant="compact"
                index={index}
                data-oid="6vk0e9x"
              />
            ))}
          </div>
        </Section>
      )}

      {/* Testimonials - Using TestimonialCard */}
      <Section
        title="What Our Customers Say"
        spacing="large"
        data-oid=".glt0p8"
      >
        <div className="grid md:grid-cols-2 gap-8" data-oid="o7fo6ol">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              quote={testimonial.quote}
              author={testimonial.author}
              role={testimonial.role}
              company={testimonial.company}
              rating={testimonial.rating}
              image={testimonial.image}
              index={index}
              data-oid="a0gdg:i"
            />
          ))}
        </div>
      </Section>

      {/* Related Features - Using FeatureCard */}
      <Section title="Related Features" spacing="large" data-oid="xl8v0n9">
        <div className="grid md:grid-cols-3 gap-6" data-oid="2zy:vq9">
          {relatedFeatures.map((relatedFeature, index) => (
            <FeatureCard
              key={relatedFeature.slug}
              icon={relatedFeature.icon}
              title={relatedFeature.title}
              description={relatedFeature.description}
              href={`/features/${relatedFeature.slug}`}
              color={relatedFeature.color}
              index={index}
              data-oid="pw33prk"
            />
          ))}
        </div>
      </Section>

      {/* CTA Section - Using CTASection */}
      <CTASection
        title={`Ready to Get Started with ${feature.title}?`}
        subtitle={`Join thousands of users who are already leveraging the power of ${feature.title}.`}
        badge={{ icon: Sparkles, text: "Transform Your Workflow" }}
        actions={[
          { text: "Start Free Trial", href: "/demo", variant: "secondary" },
          { text: "Schedule Demo", href: "/contact", variant: "outline" },
        ]}
        variant="gradient"
        showParticles={false}
        showFloatingElements={false}
        data-oid="wfd37th"
      />
    </Wrapper>
  );
};

export default FeatureDetailPage;
